import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, adminAuthHeaders } from "@/lib/api";
import { Trash2, LogOut, Mail, Smartphone, ArrowLeftRight, SlidersHorizontal, FileJson, Download, BarChart3, UsersRound, ShieldCheck, Key, Clock } from "lucide-react";
import ContentEditor from "@/components/admin/ContentEditor";
import StatusSelect from "@/components/admin/StatusSelect";
import StatsPanel from "@/components/admin/StatsPanel";
import UsersPanel from "@/components/admin/UsersPanel";
import SecurityPanel from "@/components/admin/SecurityPanel";
import ApiKeysPanel from "@/components/admin/ApiKeysPanel";
import { downloadJson } from "@/lib/orderStatus";
import { useIdleTimer, formatTime } from "@/hooks/useIdleTimer";
import { ADMIN_LOGIN_URL } from "@/lib/adminPath";

const SESSION_TIMEOUT_SEC = 10 * 60; // 10 minut nieaktywności

export default function AdminDashboard() {
  const [tab, setTab] = useState("stats");
  const [contacts, setContacts] = useState([]);
  const [simOrders, setSimOrders] = useState([]);
  const [porting, setPorting] = useState([]);
  const [selSim, setSelSim] = useState([]);
  const [selPort, setSelPort] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("rm_admin_token")) {
      nav(ADMIN_LOGIN_URL);
      return;
    }
    fetchAll();
  }, []); // eslint-disable-line

  // Auto-wyloguj przy zamknięciu karty/przegladarki (bez cleanup on unmount — StrictMode-safe)
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;

    const beforeUnloadHandler = () => {
      const token = localStorage.getItem("rm_admin_token");
      if (token && backend && "sendBeacon" in navigator) {
        const blob = new Blob([JSON.stringify({})], { type: "application/json" });
        navigator.sendBeacon(`${backend}/api/admin/logout?token=${encodeURIComponent(token)}`, blob);
      }
      localStorage.removeItem("rm_admin_token");
      localStorage.removeItem("rm_admin_role");
      localStorage.removeItem("rm_admin_user");
    };

    window.addEventListener("beforeunload", beforeUnloadHandler);
    window.addEventListener("pagehide", beforeUnloadHandler);

    return () => {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
      window.removeEventListener("pagehide", beforeUnloadHandler);
    };
  }, []); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [c, s, p] = await Promise.all([
        api.get("/admin/contacts", { headers: adminAuthHeaders() }),
        api.get("/admin/sim-orders", { headers: adminAuthHeaders() }),
        api.get("/admin/porting", { headers: adminAuthHeaders() }),
      ]);
      setContacts(c.data);
      setSimOrders(s.data);
      setPorting(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("rm_admin_token");
        nav(ADMIN_LOGIN_URL);
      }
    }
  };

  const logout = useCallback(async () => {
    try { await api.post("/admin/logout", {}, { headers: adminAuthHeaders() }); } catch {}
    localStorage.removeItem("rm_admin_token");
    localStorage.removeItem("rm_admin_role");
    localStorage.removeItem("rm_admin_user");
    nav(ADMIN_LOGIN_URL);
  }, [nav]);

  const handleIdleTimeout = useCallback(() => {
    toast.error("Sesja wygasła po 10 minutach bez aktywności");
    logout();
  }, [logout]);

  const { remaining } = useIdleTimer(SESSION_TIMEOUT_SEC, handleIdleTimeout);

  const del = async (kind, id) => {
    if (!window.confirm("Na pewno usunąć?")) return;
    try {
      await api.delete(`/admin/${kind}/${id}`, { headers: adminAuthHeaders() });
      toast.success("Usunięto");
      fetchAll();
    } catch {
      toast.error("Błąd usuwania");
    }
  };

  // kind: "sim" | "port"
  const changeStatus = async (kind, id, status) => {
    const setter = kind === "sim" ? setSimOrders : setPorting;
    setter((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
    try {
      await api.patch(`/admin/${kind}/${id}/status`, { status }, { headers: adminAuthHeaders() });
      toast.success("Status zaktualizowany");
    } catch {
      toast.error("Błąd zmiany statusu");
      fetchAll();
    }
  };

  const toggleSel = (kind, id) => {
    const [sel, setSel] = kind === "sim" ? [selSim, setSelSim] : [selPort, setSelPort];
    setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  };

  const toggleSelAll = (kind) => {
    const rows = kind === "sim" ? simOrders : porting;
    const [sel, setSel] = kind === "sim" ? [selSim, setSelSim] : [selPort, setSelPort];
    setSel(sel.length === rows.length ? [] : rows.map((r) => r.id));
  };

  const exportSelected = async (kind) => {
    const ids = kind === "sim" ? selSim : selPort;
    if (ids.length === 0) return;
    try {
      const { data } = await api.post(
        "/admin/export-selected",
        { type: kind, ids },
        { headers: adminAuthHeaders() }
      );
      downloadJson(`rmobile-${kind}-${new Date().toISOString().slice(0, 10)}.json`, data.orders);
      toast.success(`Wyeksportowano ${data.count} — oznaczono jako „Wysłane"`);
      if (kind === "sim") setSelSim([]); else setSelPort([]);
      fetchAll();
    } catch {
      toast.error("Błąd eksportu");
    }
  };

  const downloadOne = (order) => {
    downloadJson(`${order.order_number || order.id}.json`, order);
  };

  const tabs = [
    { id: "stats", label: "Statystyki", count: null, icon: BarChart3 },
    { id: "contacts", label: "Kontakty", count: contacts.length, icon: Mail },
    { id: "sim", label: "Zamówienia SIM", count: simOrders.length, icon: Smartphone },
    { id: "port", label: "Przeniesienia", count: porting.length, icon: ArrowLeftRight },
    { id: "content", label: "Cennik / Treści", count: null, icon: SlidersHorizontal },
    { id: "users", label: "Użytkownicy", count: null, icon: UsersRound },
    { id: "security", label: "Bezpieczeństwo", count: null, icon: ShieldCheck },
    { id: "api-keys", label: "Klucze API", count: null, icon: Key },
  ];

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-white">Panel administratora</h1>
          <p className="text-white/70 mt-1 text-sm">Przeglądaj i zarządzaj zgłoszeniami klientów.</p>
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="session-timer"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-mono font-semibold ${
              remaining < 60 ? "bg-red-500/20 text-red-300" : "bg-white/10 text-white/80"
            }`}
            title="Do wylogowania z powodu bezczynności"
          >
            <Clock size={14} /> {formatTime(remaining)}
          </div>
          <button data-testid="admin-logout" onClick={logout} className="rm-btn-outline inline-flex items-center gap-2">
            <LogOut size={16} /> Wyloguj
          </button>
        </div>
      </div>

      <div className="flex gap-2 mt-8 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
              tab === t.id ? "bg-[#dc2626] text-white" : "bg-white/10 text-white/80 hover:bg-white/15"
            }`}
          >
            <t.icon size={14} /> {t.label} {t.count !== null && <span className="bg-white/20 text-white rounded-full px-2 text-xs">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="rm-card mt-6 overflow-x-auto">
        {tab === "contacts" && (
          <table className="w-full text-sm" data-testid="contacts-table">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
              <tr>
                <th className="text-left p-4">Data</th>
                <th className="text-left p-4">Imię</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Telefon</th>
                <th className="text-left p-4">Temat</th>
                <th className="text-left p-4">Wiadomość</th>
                <th className="text-left p-4">RODO</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">Brak zgłoszeń</td></tr>
              )}
              {contacts.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="p-4 text-slate-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString("pl-PL")}</td>
                  <td className="p-4 font-semibold text-slate-800">{c.name}</td>
                  <td className="p-4 text-slate-600">{c.email}</td>
                  <td className="p-4 text-slate-600">{c.phone || "-"}</td>
                  <td className="p-4 text-slate-600">{c.subject || "-"}</td>
                  <td className="p-4 text-slate-700 max-w-sm">{c.message}</td>
                  <td className="p-4" data-testid={`contact-rodo-${c.id}`}>
                    {c.consent_rodo
                      ? <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">RODO ✓</span>
                      : <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">RODO ✗</span>}
                  </td>
                  <td className="p-4">
                    <button onClick={() => del("contacts", c.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === "sim" && (
          <div>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 bg-slate-50/60">
              <span className="text-sm text-slate-500">
                Zaznaczono: <strong className="text-slate-800">{selSim.length}</strong>
              </span>
              <button
                data-testid="export-sim-selected"
                onClick={() => exportSelected("sim")}
                disabled={selSim.length === 0}
                className="inline-flex items-center gap-2 bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold text-sm px-4 py-2 rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download size={15} /> Eksportuj zaznaczone (JSON)
              </button>
            </div>
            <table className="w-full text-sm" data-testid="sim-orders-table">
              <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="p-4"><input type="checkbox" data-testid="sim-select-all" checked={simOrders.length > 0 && selSim.length === simOrders.length} onChange={() => toggleSelAll("sim")} /></th>
                  <th className="text-left p-4">Nr zamówienia</th>
                  <th className="text-left p-4">Data</th>
                  <th className="text-left p-4">Klient / PESEL</th>
                  <th className="text-left p-4">Kontakt</th>
                  <th className="text-left p-4">Plan</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Adres</th>
                  <th className="text-left p-4">TERYT</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {simOrders.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-slate-400">Brak zamówień</td></tr>
                )}
                {simOrders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="p-4"><input type="checkbox" data-testid={`sim-select-${o.id}`} checked={selSim.includes(o.id)} onChange={() => toggleSel("sim", o.id)} /></td>
                    <td className="p-4 font-mono font-bold text-[#1f3a8a] whitespace-nowrap">{o.order_number || "—"}</td>
                    <td className="p-4 text-slate-500 whitespace-nowrap">{new Date(o.created_at).toLocaleString("pl-PL")}</td>
                    <td className="p-4 font-semibold text-slate-800">{o.first_name} {o.last_name}<br/><span className="text-xs text-slate-500 font-mono">PESEL: {o.pesel} ({o.gender === "K" ? "K" : "M"})</span></td>
                    <td className="p-4 text-slate-600">{o.email}<br/>{o.phone}</td>
                    <td className="p-4" data-testid={`sim-rodo-${o.id}`}>
                      <span className="px-2 py-1 bg-[#1f3a8a]/10 text-[#1f3a8a] rounded-full text-xs font-bold">{o.plan}{o.plan_code ? ` (#${o.plan_code})` : ""}</span>
                      <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-1">
                        <span className={o.consent_rodo ? "text-green-700 font-bold" : "text-red-500"}>RODO:{o.consent_rodo ? "✓" : "✗"}</span>
                        <span>M:{o.consent_marketing ? "✓" : "✗"}</span>
                        <span>E:{o.consent_electronic ? "✓" : "✗"}</span>
                      </div>
                    </td>
                    <td className="p-4"><StatusSelect testId={`sim-status-${o.id}`} value={o.status} onChange={(v) => changeStatus("sim", o.id, v)} /></td>
                    <td className="p-4 text-slate-600 text-xs">{o.address_street} {o.house_number}{o.apartment_number ? `/${o.apartment_number}` : ""}<br/>{o.address_postal} {o.address_city}{o.client_ip && <><br/><span className="text-slate-400">IP: {o.client_ip}</span></>}</td>
                    <td className="p-4 text-xs">
                      {o.teryt_simc ? (
                        <div className="space-y-0.5">
                          <div><span className="text-slate-400">SIMC:</span> <span className="font-mono text-slate-800">{o.teryt_simc}</span></div>
                          {o.teryt_ulic && <div><span className="text-slate-400">ULIC:</span> <span className="font-mono text-slate-800">{o.teryt_ulic}</span></div>}
                          {o.teryt_woj_nazwa && <div className="text-slate-500">{o.teryt_woj_nazwa}</div>}
                          {o.teryt_pow_nazwa && <div className="text-slate-500">pow. {o.teryt_pow_nazwa}</div>}
                          {o.teryt_gmi_nazwa && <div className="text-slate-500">gm. {o.teryt_gmi_nazwa}</div>}
                        </div>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <button title="Pobierz JSON" data-testid={`sim-json-${o.id}`} onClick={() => downloadOne(o)} className="text-[#1f3a8a] hover:text-[#15296b] mr-3"><FileJson size={16} /></button>
                      <button title="Usuń" onClick={() => del("sim-orders", o.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "port" && (
          <div>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 bg-slate-50/60">
              <span className="text-sm text-slate-500">
                Zaznaczono: <strong className="text-slate-800">{selPort.length}</strong>
              </span>
              <button
                data-testid="export-port-selected"
                onClick={() => exportSelected("port")}
                disabled={selPort.length === 0}
                className="inline-flex items-center gap-2 bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold text-sm px-4 py-2 rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download size={15} /> Eksportuj zaznaczone (JSON)
              </button>
            </div>
            <table className="w-full text-sm" data-testid="port-table">
              <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="p-4"><input type="checkbox" data-testid="port-select-all" checked={porting.length > 0 && selPort.length === porting.length} onChange={() => toggleSelAll("port")} /></th>
                  <th className="text-left p-4">Nr zamówienia</th>
                  <th className="text-left p-4">Data</th>
                  <th className="text-left p-4">Klient / PESEL</th>
                  <th className="text-left p-4">Email / Adres</th>
                  <th className="text-left p-4">Numer</th>
                  <th className="text-left p-4">Operator</th>
                  <th className="text-left p-4">Plan</th>
                  <th className="text-left p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {porting.length === 0 && (
                  <tr><td colSpan={10} className="p-8 text-center text-slate-400">Brak wniosków</td></tr>
                )}
                {porting.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100">
                    <td className="p-4"><input type="checkbox" data-testid={`port-select-${p.id}`} checked={selPort.includes(p.id)} onChange={() => toggleSel("port", p.id)} /></td>
                    <td className="p-4 font-mono font-bold text-[#1f3a8a] whitespace-nowrap">{p.order_number || "—"}</td>
                    <td className="p-4 text-slate-500 whitespace-nowrap">{new Date(p.created_at).toLocaleString("pl-PL")}</td>
                    <td className="p-4 font-semibold text-slate-800">{p.first_name} {p.last_name}<br/><span className="text-xs text-slate-500 font-mono">PESEL: {p.pesel} ({p.gender})</span></td>
                    <td className="p-4 text-slate-600">{p.email}<br/><span className="text-xs">{p.address_street} {p.house_number}{p.apartment_number ? "/" + p.apartment_number : ""}, {p.address_postal} {p.address_city}</span>{p.client_ip && <><br/><span className="text-xs text-slate-400">IP: {p.client_ip}</span></>}</td>
                    <td className="p-4 text-slate-700">{p.current_number}</td>
                    <td className="p-4 text-slate-600">{p.current_operator}</td>
                    <td className="p-4" data-testid={`port-rodo-${p.id}`}>
                      <span className="px-2 py-1 bg-[#1f3a8a]/10 text-[#1f3a8a] rounded-full text-xs font-bold">{p.chosen_plan}{p.plan_code ? ` (#${p.plan_code})` : ""}</span>
                      <div className="text-[10px] text-slate-500 mt-1 flex flex-wrap gap-1">
                        <span className={p.consent_rodo ? "text-green-700 font-bold" : "text-red-500"}>RODO:{p.consent_rodo ? "✓" : "✗"}</span>
                        <span>M:{p.consent_marketing ? "✓" : "✗"}</span>
                        <span>E:{p.consent_electronic ? "✓" : "✗"}</span>
                      </div>
                    </td>
                    <td className="p-4"><StatusSelect testId={`port-status-${p.id}`} value={p.status} onChange={(v) => changeStatus("port", p.id, v)} /></td>
                    <td className="p-4 whitespace-nowrap">
                      <button title="Pobierz JSON" data-testid={`port-json-${p.id}`} onClick={() => downloadOne(p)} className="text-[#1f3a8a] hover:text-[#15296b] mr-3"><FileJson size={16} /></button>
                      <button title="Usuń" onClick={() => del("porting", p.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "content" && <ContentEditor />}

        {tab === "stats" && <StatsPanel />}

        {tab === "users" && <UsersPanel />}

        {tab === "security" && <SecurityPanel />}

        {tab === "api-keys" && <ApiKeysPanel />}
      </div>
    </div>
  );
}
